import { useParams } from 'react-router-dom';
import { useChangeFlagStatus, useFlag } from '../../api/flags';
import { useIsAdmin } from '../../auth/useAuth';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { ChangelogTimeline } from '../../components/ChangelogTimeline';
import { PageHeader } from '../../components/PageHeader';
import { StatusIndicator } from '../../components/StatusIndicator';
import { Table } from '../../components/Table';
import { TargetingSummary } from '../../components/TargetingSummary';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import shared from '../shared.module.css';
import styles from './FlagDetailPage.module.css';

export function FlagDetailPage() {
	const { id, flagId } = useParams<{ id: string; flagId: string }>();
	const isAdmin = useIsAdmin();

	const { data: flag, isLoading, error } = useFlag(id ?? '', flagId ?? '');
	const changeStatus = useChangeFlagStatus(id ?? '', flagId ?? '');
	const listBreadcrumbs = useProjectBreadcrumbs('Flags');
	const detailBreadcrumbs = useProjectBreadcrumbs('Flags', flag?.name ?? '');

	async function handleToggle() {
		if (!flag) return;
		const next = flag.status === 'active' ? 'inactive' : 'active';
		await changeStatus.mutateAsync(next);
	}

	if (isLoading) {
		return (
			<div>
				<PageHeader title="Loading…" breadcrumbs={listBreadcrumbs} />
			</div>
		);
	}

	if (error || !flag) {
		return (
			<div>
				<PageHeader title="Flag" breadcrumbs={listBreadcrumbs} />
				<Card>
					<p className={shared.errorMessage}>
						{error instanceof Error ? error.message : 'Flag not found'}
					</p>
				</Card>
			</div>
		);
	}

	const variantColumns = [
		{ key: 'id', label: 'ID', mono: true },
		{ key: 'value', label: 'Value', mono: true },
		{ key: 'weight', label: 'Weight %', mono: true },
		{ key: 'payload', label: 'Payload' },
	];

	return (
		<div>
			<PageHeader
				title={flag.name}
				breadcrumbs={detailBreadcrumbs}
				actions={
					<div className={styles.headerActions}>
						<StatusIndicator status={flag.status} />
						{isAdmin && (
							<Button
								variant={flag.status === 'active' ? 'secondary' : 'primary'}
								onClick={handleToggle}
								loading={changeStatus.isPending}
							>
								{flag.status === 'active' ? 'Deactivate' : 'Activate'}
							</Button>
						)}
					</div>
				}
			/>

			<div className={shared.content}>
				{changeStatus.error && (
					<p className={shared.mutationError}>
						{changeStatus.error instanceof Error
							? changeStatus.error.message
							: 'Failed to change status'}
					</p>
				)}

				<Card active={flag.status === 'active'}>
					<h2 className={shared.cardHeading}>Details</h2>
					<dl className={shared.detailList}>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Flag ID</dt>
							<dd className={[shared.detailValue, shared.mono].join(' ')}>{flag.id}</dd>
						</div>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Type</dt>
							<dd className={shared.detailValue}>{flag.type}</dd>
						</div>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Status</dt>
							<dd className={shared.detailValue}>
								<StatusIndicator status={flag.status} />
							</dd>
						</div>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Default value</dt>
							<dd className={[shared.detailValue, shared.mono].join(' ')}>
								{String(flag.default_value)}
							</dd>
						</div>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Rollout</dt>
							<dd className={shared.detailValue}>
								<span className={[shared.detailValue, shared.mono].join(' ')}>
									{flag.rollout_percentage}%
								</span>
								<div className={styles.rolloutBar}>
									<div
										className={styles.rolloutFill}
										style={{ width: `${flag.rollout_percentage}%` }}
									/>
								</div>
							</dd>
						</div>
						<div className={shared.detailRow}>
							<dt className={shared.detailLabel}>Created by</dt>
							<dd className={[shared.detailValue, shared.mono].join(' ')}>{flag.created_by}</dd>
						</div>
					</dl>
				</Card>

				{flag.type === 'multivariate' && flag.variants.length > 0 && (
					<Card>
						<h2 className={shared.cardHeading}>Variants</h2>
						<Table>
							<Table.Header columns={variantColumns} />
							<Table.Body>
								{flag.variants.map((variant) => (
									<Table.Row key={variant.id}>
										<Table.Cell mono>{variant.id}</Table.Cell>
										<Table.Cell mono>{variant.value}</Table.Cell>
										<Table.Cell mono>{((variant.weight / 10000) * 100).toFixed(1)}%</Table.Cell>
										<Table.Cell>
											{variant.payload ? (
												<pre className={styles.inlineJson}>
													{JSON.stringify(variant.payload, null, 2)}
												</pre>
											) : (
												<span className={styles.empty}>—</span>
											)}
										</Table.Cell>
									</Table.Row>
								))}
							</Table.Body>
						</Table>
					</Card>
				)}

				<Card>
					<h2 className={shared.cardHeading}>Targeting</h2>
					<TargetingSummary
						targeting={flag.targeting}
						emptyMessage="No targeting rules — all visitors are eligible."
					/>
				</Card>

				<Card>
					<h2 className={shared.cardHeading}>Activity</h2>
					<ChangelogTimeline projectId={id ?? ''} resourceType="flag" resourceId={flagId ?? ''} />
				</Card>
			</div>
		</div>
	);
}
